import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { fmtMonthYear, fmtTime, monthMatrix, parseISO, todayISO, WEEKDAYS } from "../lib/format";
import { PlatformIcon } from "./branding";

export default function CalendarGrid({ posts, selected, onSelect }) {
  const initial = useMemo(() => {
    if (selected) return parseISO(selected);
    return new Date();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [anchor, setAnchor] = useState(initial);

  // If selection jumps to another month, follow it.
  useEffect(() => {
    if (!selected) return;
    const d = parseISO(selected);
    if (d.getMonth() !== anchor.getMonth() || d.getFullYear() !== anchor.getFullYear()) {
      setAnchor(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const cells = useMemo(() => monthMatrix(anchor), [anchor]);

  const byDate = useMemo(() => {
    const m = {};
    posts.forEach((p) => {
      (m[p.date] ||= []).push(p);
    });
    Object.values(m).forEach((list) => list.sort((a, b) => a.time.localeCompare(b.time)));
    return m;
  }, [posts]);

  const move = (delta) => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + delta, 1));
  const goToday = () => {
    const now = new Date();
    setAnchor(new Date(now.getFullYear(), now.getMonth(), 1));
    onSelect(todayISO());
  };

  return (
    <div className="bg-surface border border-border rounded-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <h3 className="text-[15px] font-semibold text-foreground">{fmtMonthYear(anchor)}</h3>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={goToday}
            className="text-[12.5px] font-medium px-2.5 py-1.5 rounded-lg text-muted hover:bg-foreground/5 transition active:scale-[0.97]"
          >
            Today
          </button>
          <button
            onClick={() => move(-1)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 transition active:scale-[0.97]"
            title="Previous month"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => move(1)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 transition active:scale-[0.97]"
            title="Next month"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-border">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-faint text-center">
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((cell, i) => {
          const dayPosts = byDate[cell.iso] || [];
          const isSelected = cell.iso === selected;
          return (
            <button
              key={cell.iso + i}
              onClick={() => onSelect(cell.iso)}
              className={`relative text-left min-h-[78px] sm:min-h-[104px] p-1.5 sm:p-2 border-b border-r border-border transition ${
                i % 7 === 6 ? "border-r-0" : ""
              } ${cell.inMonth ? "" : "bg-foreground/[0.015]"} ${
                isSelected ? "bg-foreground/[0.06]" : "hover:bg-foreground/[0.03]"
              }`}
            >
              <span
                className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[12px] font-medium ${
                  cell.isToday
                    ? "bg-accent text-accent-foreground"
                    : cell.inMonth
                      ? "text-foreground"
                      : "text-faint"
                }`}
              >
                {cell.day}
              </span>

              <div className="mt-1 space-y-1">
                {dayPosts.slice(0, 3).map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-1 rounded-md px-1 py-0.5 bg-foreground/[0.05] text-foreground/80"
                  >
                    <PlatformIcon id={p.platform} size={10} className="shrink-0 text-muted" />
                    <span className="text-[10.5px] leading-tight truncate hidden sm:inline">
                      {fmtTime(p.time)}
                    </span>
                  </div>
                ))}
                {dayPosts.length > 3 ? (
                  <div className="text-[10.5px] text-faint px-1">+{dayPosts.length - 3} more</div>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}